import React, { useEffect, useRef, useState } from "react";
import SectionText from "./SectionText";
import FooterSection from "./FooterSection";
import { useSector } from "./SectorContext";
import { useNavigate } from "react-router-dom";
import useReveal from "./hooks/useReveal";

function ProjectPage() {
  const { activeSector, selectedProject, openProject, selectedSector } =
    useSector();

  const navigate = useNavigate();
  const carouselRef = useRef(null);

  useReveal(selectedProject?.projName);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isStoryExpanded, setIsStoryExpanded] = useState(false);

  const currentProject = selectedProject;

  useEffect(() => {
    setCarouselIndex(0);
    setActiveImageIndex(0);
    setIsGalleryOpen(false);
    setIsStoryExpanded(false);

    if (selectedProject?.projName) {
      document.title = `${selectedProject.projName} | Valentino Lloyd`;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: 0,
        behavior: "auto",
      });
    }
  }, [selectedProject]);

  if (!currentProject) {
    return <p>No project selected.</p>;
  }

  const hasDocuments =
    currentProject.projDocuments && currentProject.projDocuments.length > 0;

  const storyParagraphs = String(currentProject.projStory || "")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const hasExpandableStory =
    selectedSector !== "cyber" && storyParagraphs.length > 1;

  const allProjects = activeSector.projects;

  const scrollToProject = (index) => {
    const carousel = carouselRef.current;

    if (!carousel) return;

    const tiles = carousel.querySelectorAll(".carousel-image");
    const target = tiles[index];

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  };

  const nextProject = () => {
    const nextIndex =
      carouselIndex === allProjects.length - 1 ? 0 : carouselIndex + 1;

    setCarouselIndex(nextIndex);

    requestAnimationFrame(() => {
      scrollToProject(nextIndex);
    });
  };

  const prevProject = () => {
    const prevIndex =
      carouselIndex === 0 ? allProjects.length - 1 : carouselIndex - 1;

    setCarouselIndex(prevIndex);

    requestAnimationFrame(() => {
      scrollToProject(prevIndex);
    });
  };

  const openGallery = (index) => {
    setActiveImageIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  return (
    <div className="project-page-container">
      {/* Project Header Section */}
      <div className="project-header-section">
        <div className="project-header-title">
          <p>{currentProject.projName}</p>
        </div>

        <div className="project-header-note">
          <p>{currentProject.projSpecial}</p>
        </div>
      </div>

      {/* Project Info Section */}
      <div className="project-info-section">
        <div className="project-info-side">
          <div className="info-bit">
            {selectedSector !== "cyber" ? (
              <div className="info-title">Client:</div>
            ) : (
              <div className="info-title">Environment:</div>
            )}

            <br />

            <div className="info-response">{currentProject.projClient}</div>
          </div>

          <div className="info-bit">
            <div className="info-title">Role:</div>

            <br />

            <div className="info-response">{currentProject.projRole}</div>
          </div>

          <div className="info-bit">
            <div className="info-title">Delivery:</div>

            <div className="info-response">{currentProject.projDuty}</div>
          </div>

          <div className="info-bit">
            {currentProject.projAwards.length !== 0 ? (
              <>
                <div className="info-title">Awards:</div>

                <div className="info-response">
                  {currentProject.projAwards[0].awardname}
                </div>

                <a
                  href={currentProject.projAwards[0].certlink}
                  className="award-image-link"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`View and verify ${currentProject.projAwards[0].awardname}`}
                >
                  <img
                    className="proj-award-image"
                    src={currentProject.projAwards[0].badgeimage}
                    alt={currentProject.projAwards[0].awardname}
                  />
                </a>
              </>
            ) : null}
          </div>
        </div>

        {/* Project Hero */}
        <div
          className={`project-hero-image shiny-border ${activeSector.colorClass}`}
        >
          <img
            src={currentProject.projHeroImage}
            alt={`${currentProject.projName} hero`}
            data-reveal
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>

        {/* Project Overview */}
        <div
          className={`project-overview ${
            hasExpandableStory ? "has-expandable-story" : ""
          }`}
        >
          <p>Project Overview</p>

          <div
            className={`overview-text ${
              hasExpandableStory && !isStoryExpanded ? "is-collapsed" : ""
            }`}
          >
            {hasExpandableStory ? (
              <>
                <p>{storyParagraphs[0]}</p>

                <div
                  id="project-story-more"
                  className={`project-story-more ${
                    isStoryExpanded ? "is-expanded" : ""
                  }`}
                  aria-hidden={!isStoryExpanded}
                >
                  <div className="project-story-more-inner">
                    {storyParagraphs.slice(1).map((paragraph, index) => (
                      <p key={`${currentProject.projName}-story-${index}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  className={`project-story-toggle ${activeSector.colorClass}`}
                  aria-expanded={isStoryExpanded}
                  aria-controls="project-story-more"
                  onClick={() => setIsStoryExpanded((expanded) => !expanded)}
                >
                  {isStoryExpanded ? "See less" : "See more"}
                </button>
              </>
            ) : (
              <p>{currentProject.projStory}</p>
            )}
          </div>
        </div>

        {/* Project Documents */}
        {hasDocuments && (
          <section className="project-documents-section">
            <div className="project-documents-header">
              <p>
                <strong>View</strong>
                <br />
                Reports
              </p>
            </div>

            <div className="project-documents-grid">
              {currentProject.projDocuments.map((doc, index) => (
                <a
                  key={index}
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`document-card shiny-border ${activeSector.colorClass}`}
                >
                  <div className="document-type">{doc.type}</div>
                  <div className="document-title">{doc.title}</div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Project Gallery */}
        <div className="project-gallery">
          <div className="project-gallery-heading">Project Gallery</div>

          {currentProject.projImages?.slice(0, 3).map((image, index) => (
            <button
              key={`${currentProject.projName}-${index}`}
              className={`gallery-thumb gallery-seg${index + 1}`}
              onClick={() => openGallery(index)}
            >
              <img
                src={image}
                alt={`${currentProject.projName} ${index + 1}`}
                loading="lazy"
                decoding="async"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Modal */}
      {isGalleryOpen && (
        <div className={`gallery-modal ${activeSector.colorClass}`}>
          <button
            className="gallery-close"
            onClick={closeGallery}
            aria-label="Close gallery"
          >
            ×
          </button>

          <div className="gallery-modal-title">{currentProject.projName}</div>

          <button
            className="gallery-nav prev"
            aria-label="Previous image"
            onClick={() =>
              setActiveImageIndex((prev) =>
                prev === 0 ? currentProject.projImages.length - 1 : prev - 1,
              )
            }
          >
            ‹
          </button>

          <img
            className="gallery-modal-image"
            src={currentProject.projImages[activeImageIndex]}
            alt={`${currentProject.projName} gallery ${activeImageIndex + 1}`}
          />

          <button
            className="gallery-nav next"
            aria-label="Next image"
            onClick={() =>
              setActiveImageIndex((prev) =>
                prev === currentProject.projImages.length - 1 ? 0 : prev + 1,
              )
            }
          >
            ›
          </button>
        </div>
      )}

      {/* Other Projects */}
      <section className="other-projects-section" data-reveal>
        <div className="other-projects-header">
          <p>
            <strong className={activeSector.colorClass}>Other</strong>
            <br />
            Projects
          </p>

          <div className="other-projects-carousel">
            <button onClick={prevProject}>Prev</button>

            <div className="carousel" ref={carouselRef}>
              {allProjects.map((item) => (
                <div
                  className="carousel-image"
                  key={item.projName}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    openProject(item);

                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      openProject(item);
                      window.scrollTo(0, 0);
                    }
                  }}
                >
                  <img
                    src={item.projImages[0]}
                    alt={item.projName}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>

            <button onClick={nextProject}>Next</button>
          </div>
        </div>
      </section>

      {/* Lower Page Sections */}
      <section className="bottom-section">
        <div className={`contact-band ${activeSector.colorClass}`}>
          <div className="big-bumper">
            <p>
              <strong>{activeSector.contactContent.contactHook[1]}</strong>
            </p>

            <button
              className="contact-button shiny-border"
              onClick={() => navigate("/contact")}
            >
              Let's Connect
            </button>
          </div>
        </div>

        <div className="bfooter">
          <FooterSection />
        </div>
      </section>
    </div>
  );
}

export default ProjectPage;